/**
 * 플레어 헤더 화면의 상태와 실행.
 *
 * 근거: API Standard 521, 2020년 6월판 5.5
 *
 * ── 무엇이 어디에 있나 ─────────────────────────────────
 *
 *  밸브 : 물성과 테일파이프 — **시나리오가 바뀌어도 그대로다**
 *  구간 : 관경·길이·연결 — 역시 그대로다
 *  시나리오 : 어떤 밸브가 **얼마를 흘리는가** — 이것만 바뀐다
 *
 * 이렇게 나눈 이유: 정전과 화재는 터지는 조합이 다를 뿐 배관은 같다.
 * 유량을 밸브에 두면 시나리오를 바꿀 때마다 물성까지 다시 넣게 된다.
 *
 * React 를 import 하지 않는다 — 화면 없이 시험할 수 있어야 한다.
 */
import { fittingTotals, type FittingItem } from "../calc/fittings";
import {
  solveFlareHeader,
  type FlareHeaderResult,
  type FlareValveInput,
  type HeaderSegmentInput,
} from "../calc/flareHeader";
import { PIPE_ROUGHNESS_M } from "../calc/pipeFlow";
import { innerDiameterMm } from "../data/pipes";
import { num } from "./formBits";
import { convertInput, toKpa, type PressureUnit } from "./pressureUnit";
import type { PipeMaterial } from "./pipingState";

/**
 * 완전난류 마찰계수 — Colebrook 식의 Re→∞ 극한.
 *
 *   1/√f = −2·log₁₀(ε/(3.7d))
 *
 * ⚠ **계산에는 더 이상 쓰지 않는다.** API 521 5.5.9 는 고속에서 f 가
 *   관경·조도만으로 정해지는 상수에 가까워진다고 하지만, KOSHA GUIDE
 *   D-63-2018 은 5.2.2 (라) 에서 **점도를 필수 물성으로** 요구하고
 *   5.2.6.3~4 에서 Re → Colebrook 으로 f 를 구하라고 한다. 국내 설계
 *   기준을 따르기 위해 Colebrook 으로 바꿨다.
 *
 *   비교·검증용으로 남겨 둔다. 고Re 에서 Colebrook 해가 이 값에 수렴하는지
 *   시험이 확인한다.
 */
export function fullyRoughFriction(
  innerDiameterM: number,
  roughnessM: number,
): number {
  const inv = -2 * Math.log10(roughnessM / (3.7 * innerDiameterM));
  return 1 / (inv * inv);
}

/** 화면에서 관경을 정하는 방법 — 표에서 고르거나 직접 넣거나. */
export interface FlarePipeSpec {
  nps: string;
  schedule: string;
  useCustomId: boolean;
  customIdMm: string;
  material: PipeMaterial;
  /** 직관 길이 (m) */
  straightLengthM: string;
  fittings: FittingItem[];
  /**
   * 부속 등가길이를 직관 길이의 몇 %로 잡을지 (예비 검토용).
   * 표준은 실제 배치 기준으로 **직관의 100 % 이상**이 되는 경우가
   * 흔하다고 한다(5.5.9). 빈 값이면 부속 목록으로만 계산한다.
   */
  fittingAllowancePercent: string;
}

export interface FlareValveState {
  readonly id: string;
  tag: string;
  molecularWeight: string;
  temperatureK: string;
  compressibility: string;
  /** 절대점도 (cP) — KOSHA D-63 5.2.2 (라) 가 요구하는 필수 물성 */
  viscosityCp: string;
  /** 붙는 헤더 구간 id */
  segmentId: string;
  tail: FlarePipeSpec;
}

export interface FlareSegmentState {
  readonly id: string;
  label: string;
  /** 흘러 나가는 하류 구간 id. null 이면 플레어 팁에 직접 닿는다. */
  downstreamId: string | null;
  pipe: FlarePipeSpec;
}

/** 동시 분출 조합 하나. */
export interface FlareScenarioState {
  readonly id: string;
  name: string;
  /** 밸브 id → 유량 (kg/h). 없거나 0 이면 이 시나리오에서 분출하지 않는다. */
  flows: Record<string, string>;
}

export interface FlareState {
  /** 플레어 팁(계통 출구) 압력 — 사용자 단위 게이지 */
  tipPressure: string;
  /** 마하수 상한 */
  machLimit: string;
  valves: FlareValveState[];
  segments: FlareSegmentState[];
  scenarios: FlareScenarioState[];
  /** 화면에서 보고 있는 시나리오 */
  activeScenarioId: string;
}

export const FLARE_PRESSURE_FIELDS = ["tipPressure"] as const;

let seq = 0;
const nextId = (p: string) => `${p}-${(seq += 1)}`;

export function newFlarePipe(
  nps: string,
  straightLengthM: string,
): FlarePipeSpec {
  return {
    nps,
    schedule: "40",
    useCustomId: false,
    customIdMm: "",
    material: "commercialSteel",
    straightLengthM,
    fittings: [{ kind: "elbow90-long", count: 2 }],
    fittingAllowancePercent: "",
  };
}

export function newFlareValve(tag: string, segmentId: string): FlareValveState {
  return {
    id: nextId("fv"),
    tag,
    molecularWeight: "30",
    temperatureK: "320",
    compressibility: "1",
    viscosityCp: "0.012",
    segmentId,
    tail: newFlarePipe("8", "20"),
  };
}

export function newFlareSegment(
  label: string,
  downstreamId: string | null,
): FlareSegmentState {
  return {
    id: nextId("fs"),
    label,
    downstreamId,
    pipe: newFlarePipe("16", "50"),
  };
}

export function newFlareScenario(
  name: string,
  flows: Record<string, string> = {},
): FlareScenarioState {
  return { id: nextId("sc"), name, flows };
}

/**
 * 기본 상태 — 지선 두 개가 주헤더로 모이는 형태.
 *
 *   PSV-101 ┐
 *           ├─ 지선 A ─┐
 *   PSV-102 ┘          ├─ 주헤더 ─▶ 팁
 *   PSV-103 ─ 지선 B ──┘
 *
 * 1단계의 한 줄짜리 대신 갈라지는 형태를 기본으로 둔다 — 2단계에서
 * 무엇이 새로 되는지 열자마자 보이게 하려는 것이다.
 */
export function newFlareState(): FlareState {
  const main = newFlareSegment("주헤더", null);
  const a = newFlareSegment("지선 A", main.id);
  const b = newFlareSegment("지선 B", main.id);
  a.pipe = newFlarePipe("12", "30");
  b.pipe = newFlarePipe("10", "40");

  const v1 = newFlareValve("PSV-101", a.id);
  const v2 = newFlareValve("PSV-102", a.id);
  const v3 = newFlareValve("PSV-103", b.id);

  const blackout = newFlareScenario("전 공장 정전", {
    [v1.id]: "20000",
    [v2.id]: "20000",
    [v3.id]: "20000",
  });
  const fire = newFlareScenario("A구역 화재", {
    [v1.id]: "26000",
    [v2.id]: "26000",
    [v3.id]: "0",
  });

  return {
    tipPressure: "10",
    machLimit: "0.5",
    valves: [v1, v2, v3],
    segments: [a, b, main],
    scenarios: [blackout, fire],
    activeScenarioId: blackout.id,
  };
}

export function convertFlarePressures(
  s: FlareState,
  from: PressureUnit,
  to: PressureUnit,
): FlareState {
  if (from === to) return s;
  return { ...s, tipPressure: convertInput(s.tipPressure, from, to) };
}

/** 화면이 고른 방식으로 내경(m)을 정한다. */
export function pipeInnerDiameterM(p: FlarePipeSpec): number {
  if (p.useCustomId) return num(p.customIdMm) / 1000;
  const mm = innerDiameterMm(p.nps, p.schedule);
  return mm === null ? Number.NaN : mm / 1000;
}

/**
 * 등가길이 = 직관 + 부속.
 *
 * 부속은 두 가지로 넣을 수 있고 둘 다 쓰면 더해진다.
 *   ① 부속 목록 — 종류·개수로 정확히
 *   ② 여유율(%) — 예비 검토에서 직관의 몇 %로 뭉뚱그려 (5.5.9)
 *
 * ⚠ 대기 방출이어도 **출구 손실 K=1 을 넣지 않는다.** 등온식 유도에
 *   이미 들어 있다(5.5.9).
 */
export function pipeEquivalentLengthM(p: FlarePipeSpec, dM: number): number {
  const straight = num(p.straightLengthM);
  if (!Number.isFinite(straight)) return Number.NaN;
  const totals = fittingTotals(p.fittings, dM);
  const allowance = num(p.fittingAllowancePercent);
  const extra = Number.isFinite(allowance) ? (straight * allowance) / 100 : 0;
  return straight + totals.equivalentLengthM + extra;
}

/** 이 시나리오에서 그 밸브가 흘리는 유량 문자열. 없으면 "0". */
export function scenarioFlow(
  scenario: FlareScenarioState | undefined,
  valveId: string,
): string {
  return scenario?.flows[valveId] ?? "0";
}

export function findScenario(
  s: FlareState,
  id: string,
): FlareScenarioState | undefined {
  return s.scenarios.find((x) => x.id === id);
}

export interface FlareDerived {
  readonly id: string;
  readonly innerDiameterMm: number;
  readonly equivalentLengthM: number;
  /** 절대조도 (mm) — 마찰계수는 유량이 정해져야 나오므로 결과 쪽에 있다 */
  readonly roughnessMm: number;
}

export interface FlareOutcome {
  readonly result: FlareHeaderResult;
  readonly derived: readonly FlareDerived[];
}

function specToDerived(p: FlarePipeSpec) {
  const dM = pipeInnerDiameterM(p);
  return {
    innerDiameterM: dM,
    equivalentLengthM: pipeEquivalentLengthM(p, dM),
    roughnessM: PIPE_ROUGHNESS_M[p.material],
  };
}

/** 한 시나리오를 푼다. */
export function runFlare(
  s: FlareState,
  unit: PressureUnit,
  scenarioId: string = s.activeScenarioId,
): FlareOutcome {
  const scenario = findScenario(s, scenarioId);

  const derived: FlareDerived[] = [
    ...s.segments.map((seg) => {
      const d = specToDerived(seg.pipe);
      return {
        id: seg.id,
        innerDiameterMm: d.innerDiameterM * 1000,
        equivalentLengthM: d.equivalentLengthM,
        roughnessMm: d.roughnessM * 1000,
      };
    }),
    ...s.valves.map((v) => {
      const d = specToDerived(v.tail);
      return {
        id: v.id,
        innerDiameterMm: d.innerDiameterM * 1000,
        equivalentLengthM: d.equivalentLengthM,
        roughnessMm: d.roughnessM * 1000,
      };
    }),
  ];

  const segments: HeaderSegmentInput[] = s.segments.map((seg) => {
    const d = specToDerived(seg.pipe);
    return {
      id: seg.id,
      label: seg.label,
      innerDiameterM: d.innerDiameterM,
      equivalentLengthM: d.equivalentLengthM,
      roughnessM: d.roughnessM,
      downstreamId: seg.downstreamId,
    };
  });

  const valves: FlareValveInput[] = s.valves.map((v) => {
    const d = specToDerived(v.tail);
    return {
      id: v.id,
      tag: v.tag,
      massFlowKgH: num(scenarioFlow(scenario, v.id)),
      molecularWeight: num(v.molecularWeight),
      temperatureK: num(v.temperatureK),
      compressibility: num(v.compressibility),
      viscosityCp: num(v.viscosityCp),
      tailInnerDiameterM: d.innerDiameterM,
      tailEquivalentLengthM: d.equivalentLengthM,
      tailRoughnessM: d.roughnessM,
      segmentId: v.segmentId,
    };
  });

  const result = solveFlareHeader({
    // 팁 압력은 게이지로 받아 절대압으로 넘긴다.
    tipPressureKpaAbs: toKpa(num(s.tipPressure), unit) + 101.325,
    valves,
    segments,
    machLimit: num(s.machLimit),
  });

  return { result, derived };
}

/* ── 시나리오 비교 ────────────────────────────────────────
 * 헤더 관경을 정하는 것은 **가장 나쁜 조합**이다. 시나리오를 하나씩
 * 보는 것만으로는 어느 것이 지배하는지 알 수 없다.
 */

export interface ValveWorstCase {
  readonly valveId: string;
  readonly tag: string;
  /** 가장 높은 배압 (kPag) */
  readonly backpressureKpaGauge: number;
  /** 그 배압을 만든 시나리오 */
  readonly scenarioId: string;
  readonly scenarioName: string;
}

export interface ScenarioSummary {
  readonly scenarioId: string;
  readonly name: string;
  readonly ok: boolean;
  readonly message?: string;
  readonly totalFlowKgH: number;
  /** 밸브 id → 배압 (kPag) */
  readonly backpressures: ReadonlyMap<string, number>;
  readonly maxHeaderMach: number;
  readonly machExceeded: boolean;
}

export interface FlareComparison {
  readonly summaries: readonly ScenarioSummary[];
  /** 밸브마다 가장 나쁜 시나리오 */
  readonly worstPerValve: readonly ValveWorstCase[];
}

/** 모든 시나리오를 풀어 밸브별 최악값을 뽑는다. */
export function compareScenarios(
  s: FlareState,
  unit: PressureUnit,
): FlareComparison {
  const summaries: ScenarioSummary[] = [];
  const worst = new Map<string, ValveWorstCase>();

  for (const sc of s.scenarios) {
    const { result } = runFlare(s, unit, sc.id);
    if (!result.ok) {
      summaries.push({
        scenarioId: sc.id,
        name: sc.name,
        ok: false,
        message: result.message,
        totalFlowKgH: 0,
        backpressures: new Map(),
        maxHeaderMach: Number.NaN,
        machExceeded: false,
      });
      continue;
    }

    const backpressures = new Map<string, number>();
    for (const v of result.valves) {
      backpressures.set(v.id, v.backpressureKpaGauge);
      const prev = worst.get(v.id);
      if (prev === undefined || v.backpressureKpaGauge > prev.backpressureKpaGauge) {
        worst.set(v.id, {
          valveId: v.id,
          tag: v.tag,
          backpressureKpaGauge: v.backpressureKpaGauge,
          scenarioId: sc.id,
          scenarioName: sc.name,
        });
      }
    }

    summaries.push({
      scenarioId: sc.id,
      name: sc.name,
      ok: true,
      totalFlowKgH: result.totalFlowKgH,
      backpressures,
      maxHeaderMach: Math.max(...result.segments.map((x) => x.outletMach)),
      machExceeded:
        result.segments.some((x) => x.machExceeded) ||
        result.valves.some((x) => x.machExceeded),
    });
  }

  // 화면에 밸브 순서대로 보이도록 정렬한다.
  const worstPerValve = s.valves
    .map((v) => worst.get(v.id))
    .filter((w): w is ValveWorstCase => w !== undefined);

  return { summaries, worstPerValve };
}
